// frontend/src/services/notificationService.js
// Browser notification service for chat messages
// HIPAA-compliant: No PHI in notification content

class NotificationService {
  constructor() {
    this.permission = "default";
    this.isSupported = "Notification" in window;
    this.isEnabled = false;
    this.soundEnabled = true;
    this.notificationSound = null;

    // Initialize
    this.init();
  }

  /**
   * Initialize the notification service
   */
  init() {
    if (!this.isSupported) {
      console.warn("Browser notifications not supported");
      return;
    }

    // Check current permission
    this.permission = Notification.permission;
    this.isEnabled = this.permission === "granted";

    // Load user preferences from localStorage
    this.loadPreferences();

    // Preload notification sound
    this.loadSound();
  }

  /**
   * Load user notification preferences
   */
  loadPreferences() {
    try {
      const prefs = localStorage.getItem("chat_notification_prefs");
      if (prefs) {
        const parsed = JSON.parse(prefs);
        this.soundEnabled = parsed.soundEnabled ?? true;
      }
    } catch (err) {
      console.warn("Failed to load notification preferences:", err);
    }
  }

  /**
   * Save user notification preferences
   */
  savePreferences() {
    try {
      localStorage.setItem(
        "chat_notification_prefs",
        JSON.stringify({
          soundEnabled: this.soundEnabled,
        })
      );
    } catch (err) {
      console.warn("Failed to save notification preferences:", err);
    }
  }

  /**
   * Load notification sound
   */
  loadSound() {
    try {
      this.notificationSound = new Audio("/sounds/notification.mp3");
      this.notificationSound.volume = 0.5;

      // Add error handler for missing file
      this.notificationSound.addEventListener("error", (e) => {
        console.warn("Notification sound file not found - sound disabled");
        this.notificationSound = null;
      });

      this.notificationSound.load();
    } catch (err) {
      console.warn("Failed to load notification sound:", err);
      this.notificationSound = null;
    }
  }

  /**
   * Request permission for browser notifications
   * @returns {Promise<boolean>} Whether permission was granted
   */
  async requestPermission() {
    if (!this.isSupported) {
      return false;
    }

    if (this.permission === "granted") {
      this.isEnabled = true;
      return true;
    }

    if (this.permission === "denied") {
      console.warn("Notification permission was previously denied");
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      this.permission = result;
      this.isEnabled = result === "granted";
      return this.isEnabled;
    } catch (err) {
      console.error("Failed to request notification permission:", err);
      return false;
    }
  }

  /**
   * Show a browser notification
   * HIPAA Note: We don't include message content in notifications
   * @param {Object} options Notification options
   */
  showNotification(options) {
    const {
      title = "New Message",
      body = "You have a new message",
      icon = "/logo192.png",
      tag = "chat-message",
      onClick = null,
      senderName = null,
    } = options;

    // Check if we should show notification
    if (!this.isSupported || !this.isEnabled) {
      return null;
    }

    // Don't show if document is visible/focused
    if (document.visibilityState === "visible" && document.hasFocus()) {
      // Just play sound if enabled
      this.playSound();
      return null;
    }

    try {
      // HIPAA-compliant notification (no PHI)
      const notificationTitle = senderName
        ? `Message from ${senderName}`
        : title;

      const notification = new Notification(notificationTitle, {
        body: body,
        icon: icon,
        tag: tag, // Prevents duplicate notifications
        badge: "/logo192.png",
        vibrate: [200, 100, 200],
        requireInteraction: false,
        silent: false,
      });

      // Handle click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        notification.close();

        if (onClick && typeof onClick === "function") {
          onClick();
        }
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Play sound
      this.playSound();

      return notification;
    } catch (err) {
      console.error("Failed to show notification:", err);
      return null;
    }
  }

  /**
   * Play notification sound
   */
  playSound() {
    if (!this.soundEnabled || !this.notificationSound) {
      return;
    }

    try {
      // Reset and play
      this.notificationSound.currentTime = 0;
      this.notificationSound.play().catch((err) => {
        // Ignore autoplay errors (user hasn't interacted with page)
        if (err.name !== "NotAllowedError") {
          console.warn("Failed to play notification sound:", err);
        }
      });
    } catch (err) {
      console.warn("Failed to play notification sound:", err);
    }
  }

  /**
   * Enable/disable sound
   * @param {boolean} enabled
   */
  setSoundEnabled(enabled) {
    this.soundEnabled = enabled;
    this.savePreferences();
  }

  /**
   * Check if notifications are supported and enabled
   * @returns {Object} Status object
   */
  getStatus() {
    return {
      isSupported: this.isSupported,
      permission: this.permission,
      isEnabled: this.isEnabled,
      soundEnabled: this.soundEnabled,
    };
  }

  /**
   * Show a new message notification
   * @param {Object} message The message object
   * @param {Function} onClickCallback Callback when notification is clicked
   */
  notifyNewMessage(message, onClickCallback = null) {
    if (!message) return;

    // HIPAA: Don't include actual message content
    this.showNotification({
      title: "New Message",
      body: "You have received a new message",
      senderName: message.sender_name || null,
      tag: `chat-${message.conversation_id || "new"}`,
      onClick: onClickCallback,
    });
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
